import { Component, AfterViewInit, inject, PLATFORM_ID, ElementRef, ViewChild, OnDestroy, signal } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import * as L from 'leaflet';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

interface PointOfInterest {
    id: string;
    title: string;
    description: string;
    rating: number; // 1-5
    reviews: number;
    coords: [number, number];
    image?: string;
    tags: string[];
}

@Component({
    selector: 'app-map',
    standalone: true,
    imports: [CommonModule, TranslateModule],
    template: `
    <div class="relative w-full h-[450px] rounded-2xl overflow-hidden shadow-2xl group">
        <!-- Map Container -->
        <div #map id="map" class="absolute inset-0 z-0 transition-transform duration-700 ease-in-out"></div>

        <!-- Overlay Gradient for text readability (optional, subtle) -->
        <div class="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent pointer-events-none z-10 w-full h-full"></div>

        <!-- Floating Cards Container -->
        <div class="absolute bottom-0 left-0 w-full p-6 z-20 flex flex-nowrap overflow-x-auto gap-4 md:flex md:flex-col md:w-auto md:top-6 md:bottom-6 md:right-6 md:left-auto md:overflow-y-auto md:overflow-x-hidden pointer-events-auto scrollbar-hide">
            
            @for (poi of pois; track poi.id) {
                <div 
                    (mouseenter)="flyTo(poi)"
                    (click)="openGoogleMaps(poi)"
                    [class.ring-2]="activePoi()?.id === poi.id"
                    [class.ring-enduro-orange]="activePoi()?.id === poi.id"
                    [class.scale-105]="activePoi()?.id === poi.id"
                    class="bg-white border-2 border-gray-100 p-5 rounded-2xl shadow-xl cursor-pointer transform hover:scale-105 transition-all duration-300 w-72 flex-shrink-0 md:w-80 group/card relative overflow-hidden flex-shrink-0">
                    
                    <div class="flex justify-between items-start mb-2">
                        <h3 class="text-gray-900 font-bold text-lg leading-tight group-hover/card:text-enduro-orange transition-colors">{{ poi.title }}</h3>
                        <div class="flex items-center bg-gray-100 px-2 py-1 rounded text-gray-700 text-xs font-bold border border-gray-200">
                            <svg class="w-3 h-3 mr-1 fill-yellow-500" viewBox="0 0 24 24"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg>
                            {{ poi.rating }}
                        </div>
                    </div>
                    
                    <p class="text-gray-600 text-sm mb-4 line-clamp-2 leading-relaxed">{{ poi.description }}</p>
                    
                    <div class="flex gap-2 mb-4">
                        @for (tag of poi.tags; track tag) {
                            <span class="text-[10px] uppercase tracking-wider text-gray-500 bg-gray-100 px-2 py-1 rounded font-medium">{{ tag }}</span>
                        }
                    </div>

                    <div class="flex items-center text-gray-400 text-xs border-t border-gray-100 pt-3">
                        <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z"></path></svg>
                        <span class="font-medium text-gray-500">{{ poi.reviews }} recensioni</span>
                    </div>
                </div>
            }

        </div>
    </div>
  `,
    styles: [`
    /* Hide leaflet attribution prefix to clean up look, keep copyright */
    ::ng-deep .leaflet-control-attribution {
        background: rgba(0,0,0,0.5) !important;
        color: #aaa !important;
        text-decoration: none !important;
    }
    ::ng-deep .leaflet-control-attribution a {
        color: #ccc !important;
    }
    /* Hide scrollbar for Chrome, Safari and Opera */
    .scrollbar-hide::-webkit-scrollbar {
        display: none;
    }
    /* Hide scrollbar for IE, Edge and Firefox */
    .scrollbar-hide {
        -ms-overflow-style: none;  /* IE and Edge */
        scrollbar-width: none;  /* Firefox */
    }
    /* POI Label Styling */
    ::ng-deep .poi-label {
        background: rgba(0, 0, 0, 0.75) !important;
        border: none !important;
        border-radius: 4px !important;
        color: white !important;
        font-weight: bold !important;
        font-size: 11px !important;
        padding: 4px 8px !important;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3) !important;
        text-transform: uppercase !important;
        letter-spacing: 0.5px !important;
    }
    ::ng-deep .poi-label::before {
        display: none !important;
    }
  `]
})
export class MapComponent implements AfterViewInit, OnDestroy {
    private platformId = inject(PLATFORM_ID);
    private translate = inject(TranslateService);
    @ViewChild('map') mapContainer!: ElementRef;

    private map: L.Map | undefined;
    activePoi = signal<PointOfInterest | null>(null);

    pois: PointOfInterest[] = [];

    constructor() {
        this.loadPOIs();
        this.translate.onLangChange.subscribe(() => {
            this.loadPOIs();
        });
    }

    private loadPOIs() {
        this.pois = [
            {
                id: 'palai',
                title: this.translate.instant('MAP.POIS.PALAI.TITLE'),
                description: this.translate.instant('MAP.POIS.PALAI.DESCRIPTION'),
                rating: 4.8,
                reviews: 124,
                coords: [40.3388431, 8.9188539],
                tags: [
                    this.translate.instant('MAP.POIS.PALAI.TAGS.MOUNTAIN'),
                    this.translate.instant('MAP.POIS.PALAI.TAGS.VIEW'),
                    this.translate.instant('MAP.POIS.PALAI.TAGS.HARD_ENDURO')
                ]
            },
            {
                id: 'piercy',
                title: this.translate.instant('MAP.POIS.PIERCY.TITLE'),
                description: this.translate.instant('MAP.POIS.PIERCY.DESCRIPTION'),
                rating: 4.9,
                reviews: 215,
                coords: [40.3488, 8.8748],
                tags: [
                    this.translate.instant('MAP.POIS.PIERCY.TAGS.HISTORY'),
                    this.translate.instant('MAP.POIS.PIERCY.TAGS.NATURE'),
                    this.translate.instant('MAP.POIS.PIERCY.TAGS.FAST_TRAILS')
                ]
            },
            {
                id: 'mularza',
                title: this.translate.instant('MAP.POIS.MULARZA.TITLE'),
                description: this.translate.instant('MAP.POIS.MULARZA.DESCRIPTION'),
                rating: 4.7,
                reviews: 98,
                coords: [40.3566707, 8.9106246],
                tags: [
                    this.translate.instant('MAP.POIS.MULARZA.TAGS.WATER'),
                    this.translate.instant('MAP.POIS.MULARZA.TAGS.NATURE'),
                    this.translate.instant('MAP.POIS.MULARZA.TAGS.TECHNICAL')
                ]
            }
        ];

        if (this.map) {
            this.updateMapMarkers();
        }
    }

    async ngAfterViewInit() {
        if (isPlatformBrowser(this.platformId)) {
            // Wait for translations to be loaded
            this.translate.get('MAP.POIS.PALAI.TITLE').subscribe(() => {
                this.loadPOIs();
                this.initMap();
            });
        }
    }

    private markerLayerGroup: L.LayerGroup | undefined;

    private initMap(): void {
        const orangeIcon = L.divIcon({
            className: 'custom-pin',
            html: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#FF6600" stroke="#000000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="w-10 h-10 drop-shadow-lg filter drop-shadow(0 4px 3px rgb(0 0 0 / 0.5))">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                <circle cx="12" cy="10" r="3" fill="white"></circle>
              </svg>`,
            iconSize: [40, 40],
            iconAnchor: [20, 40],
            popupAnchor: [0, -40]
        });

        // Voyager Map
        this.map = L.map(this.mapContainer.nativeElement, {
            zoomControl: false, // Clean UI
            scrollWheelZoom: false // Avoid annoying scroll blocking
        });

        L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png', {
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>',
            subdomains: 'abcd',
            maxZoom: 20
        }).addTo(this.map);

        this.markerLayerGroup = L.layerGroup().addTo(this.map);
        this.updateMapMarkers();

        // Default activate first (conceptually, but don't fly to it yet)
        if (this.pois.length > 0) {
            this.activePoi.set(this.pois[0]);
        }

        setTimeout(() => {
            this.map?.invalidateSize();
            // Fit bounds to show all markers with padding
            if (this.pois.length > 0) {
                const bounds = L.latLngBounds(this.pois.map(p => p.coords));
                this.map?.fitBounds(bounds, { padding: [50, 50], maxZoom: 13 });
            }
        }, 200);
    }

    private updateMapMarkers() {
        if (!this.map || !this.markerLayerGroup) return;

        this.markerLayerGroup.clearLayers();

        const orangeIcon = L.divIcon({
            className: 'custom-pin',
            html: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#FF6600" stroke="#000000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="w-10 h-10 drop-shadow-lg filter drop-shadow(0 4px 3px rgb(0 0 0 / 0.5))">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                <circle cx="12" cy="10" r="3" fill="white"></circle>
              </svg>`,
            iconSize: [40, 40],
            iconAnchor: [20, 40],
            popupAnchor: [0, -40]
        });

        this.pois.forEach(poi => {
            const marker = L.marker(poi.coords, { icon: orangeIcon });

            // Add permanent label showing POI name
            // Ensure we use the translated title
            marker.bindTooltip(poi.title.split('(')[0].trim(), {
                permanent: true,
                direction: 'top',
                className: 'poi-label',
                offset: [0, -35]
            });

            marker.on('click', () => {
                this.activePoi.set(poi);
                this.flyTo(poi);
            });

            marker.addTo(this.markerLayerGroup!);
        });
    }

    flyTo(poi: PointOfInterest) {
        this.activePoi.set(poi);
        this.map?.flyTo(poi.coords, 14, {
            duration: 2,
            easeLinearity: 0.25
        });
    }

    openGoogleMaps(poi: PointOfInterest) {
        const url = `https://www.google.com/maps/search/?api=1&query=${poi.coords[0]},${poi.coords[1]}`;
        window.open(url, '_blank');
    }

    ngOnDestroy() {
        this.map?.remove();
    }
}
